import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { Decimal } from 'decimal.js';
import { isDecimalField, unwrapSchema } from '@apexdesigner/schema-tools';

/**
 * Decimal fields (a Decimal-backed base type such as Currency) travel as
 * decimal strings. Returns a copy of `value` with every decimal field —
 * including those in embedded objects, arrays and included relationships —
 * turned into a decimal.js Decimal. Values that aren't decimal text are left
 * as they are, and `value` itself is not changed.
 */
export function decodeDecimals(schema: any, value: any): any {
  if (!schema || value === null || value === undefined) return value;

  if (isDecimalField(schema)) {
    if (
      value instanceof Decimal ||
      (typeof value !== 'string' && typeof value !== 'number')
    )
      return value;
    try {
      return new Decimal(value);
    } catch {
      return value;
    }
  }

  const inner = unwrapSchema(schema);
  if (inner instanceof z.ZodArray) {
    return Array.isArray(value)
      ? value.map((item) => decodeDecimals(inner.element, item))
      : value;
  }
  if (
    inner instanceof z.ZodObject &&
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    const shape = inner.shape as Record<string, unknown>;
    const result: Record<string, any> = { ...value };
    for (const key of Object.keys(result)) {
      if (key in shape) result[key] = decodeDecimals(shape[key], result[key]);
    }
    return result;
  }
  return value;
}

export class BusinessObjectBase {
  static httpClient: HttpClient;
  static baseUrl = '/api';

  static configure(httpClient: HttpClient): void {
    BusinessObjectBase.httpClient = httpClient;
  }

  constructor(data?: any) {
    // Every API read passes through here, so decimal fields become Decimal
    // for callers regardless of which method fetched them
    if (data)
      Object.assign(
        this,
        decodeDecimals((this.constructor as any).schema, data),
      );
  }

  protected static get<T>(
    url: string,
    params?: Record<string, string>,
    headers?: Record<string, string>,
  ): Promise<T> {
    let httpParams = new HttpParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        httpParams = httpParams.set(key, value);
      }
    }
    return firstValueFrom(
      this.httpClient.get<T>(url, {
        params: httpParams,
        headers: headers ? new HttpHeaders(headers) : undefined,
      }),
    );
  }

  protected static post<T>(
    url: string,
    body: any,
    headers?: Record<string, string>,
  ): Promise<T> {
    return firstValueFrom(
      this.httpClient.post<T>(url, body, {
        headers: headers ? new HttpHeaders(headers) : undefined,
      }),
    );
  }

  protected static patch<T>(
    url: string,
    body: any,
    headers?: Record<string, string>,
  ): Promise<T> {
    return firstValueFrom(
      this.httpClient.patch<T>(url, body, {
        headers: headers ? new HttpHeaders(headers) : undefined,
      }),
    );
  }

  protected static del<T>(
    url: string,
    headers?: Record<string, string>,
  ): Promise<T> {
    return firstValueFrom(
      this.httpClient.delete<T>(url, {
        headers: headers ? new HttpHeaders(headers) : undefined,
      }),
    );
  }
}
